import { CircularProgress } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { WorkflowDto } from 'src/generated/services/TFinancialApi';
import { useWorkflowList, useWorkflowById } from 'src/use-cases/settings/useWorkflows';
import Button from 'src/presentations/components/atom/button';
import AddIcon from '@mui/icons-material/Add';
import { SettingsMain } from '../SettingsMain';
import { AuditWorkflowStats } from './AuditWorkflowStats';
import { AuditWorkflowTable } from './AuditWorkflowTable';
import { WorkflowRuleDrawer } from './WorkflowRuleDrawer';
import { WorkflowModal } from './WorkflowModal';
import { WorkflowDropdown } from './WorkflowDropdown';

interface AuditWorkflowTableProps {
  searchQuery?: string;
}

export const AuditWorkflowMain: FC<AuditWorkflowTableProps> = (props): JSX.Element => {
  const { searchQuery } = props;

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  const [workflowToEdit, setWorkflowToEdit] = useState<WorkflowDto | null>(null);
  const [workflowRuleTable, setWorkflowRuleTable] = useState<any>(null);

  const handleWorkflowSelect = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
  };

  const handleAddNew = () => {
    setWorkflowToEdit(null);
    setIsWorkflowModalOpen(true);
  };

  const handleEdit = (workflow: WorkflowDto) => {
    setWorkflowToEdit(workflow);
    setIsWorkflowModalOpen(true);
  };

  // Fetch workflows list (for dropdown)
  const { data: workflowsData, isLoading: isLoadingWorkflows, refetch: refetchWorkflows } = useWorkflowList();

  // Fetch selected workflow by ID (includes rules)
  const { data: selectedWorkflowData, isLoading: isLoadingSelectedWorkflow, refetch: refetchSelectedWorkflow } = useWorkflowById(selectedWorkflowId);

  // Auto-select first workflow when data loads
  useEffect(() => {
    if (!selectedWorkflowId && workflowsData?.data && workflowsData.data.length > 0) {
      const firstWorkflow = workflowsData.data[0];
      if (firstWorkflow.id) {
        setSelectedWorkflowId(firstWorkflow.id);
      }
    }
  }, [workflowsData, selectedWorkflowId]);

  // Get selected workflow object from the detail API
  const selectedWorkflow = useMemo(() => selectedWorkflowData?.data, [selectedWorkflowData]);

  // Get workflow rules from the selected workflow (already included in WorkflowDto)
  const selectedWorkflowRules = useMemo(() => {
    if (!selectedWorkflow) return [];
    return selectedWorkflow.workflowRules || [];
  }, [selectedWorkflow]);

  const handleRefetch = () => {
    refetchWorkflows();
    refetchSelectedWorkflow();
  };

  return (
    <SettingsMain
      title='Audit Workflow Configurations'
      subtitle='Manage the audit workflow configurations'
      backPath='/settings'
      topActions={
        <div className='flex items-center gap-12'>
          <WorkflowDropdown
            workflows={workflowsData?.data || []}
            selectedWorkflowId={selectedWorkflowId}
            onWorkflowSelect={handleWorkflowSelect}
            onAddNew={handleAddNew}
            onEdit={handleEdit}
          />
          <Button
            variant='primary'
            className='!pr-12 !pl-16'
            onClick={() => {
              if (workflowRuleTable) {
                workflowRuleTable.setCreatingRow(true);
              }
            }}
          >
            <div className='flex items-center gap-10'>
              <span className='text-14 xl:text-16'>Add New</span>
              <AddIcon />
            </div>
          </Button>
        </div>
      }
    >
      {(isLoadingWorkflows || isLoadingSelectedWorkflow) && (
        <div className='flex justify-center items-center py-20 px-16 xl:px-24'>
          <CircularProgress size={40} />
        </div>
      )}
      {!isLoadingWorkflows && !isLoadingSelectedWorkflow && selectedWorkflowId && (
        <>
          <AuditWorkflowStats workflow={selectedWorkflow} workflowRules={selectedWorkflowRules} />
          <AuditWorkflowTable
            workflowId={selectedWorkflowId}
            workflowRules={selectedWorkflowRules}
            isLoading={isLoadingWorkflows}
            onRefetch={handleRefetch}
            onTableReady={setWorkflowRuleTable}
          />
        </>
      )}
      {!isLoadingWorkflows && !isLoadingSelectedWorkflow && !selectedWorkflowId && (
        <div className='text-neutral-60 py-20 px-16 xl:px-24'>Please select a workflow to view details</div>
      )}

      <WorkflowRuleDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} workflowRule={null} onSuccess={handleRefetch} />

      <WorkflowModal
        isOpen={isWorkflowModalOpen}
        onClose={() => {
          setIsWorkflowModalOpen(false);
          setWorkflowToEdit(null);
        }}
        workflow={workflowToEdit}
        onSuccess={handleRefetch}
      />
    </SettingsMain>
  );
};
